import {
    ChevronDownIcon,
    CheckIcon,
} from '@heroicons/react/24/outline/index.js';
import { Children, useEffect, useState } from 'react';
import { formatCurrency } from '../../../../lib/CurrencyHelpers.js';
import SelectionSelectedBlock from '~/components/forms/questions/scheduling/SelectionSelectedBlock.jsx';
import { truncate } from '~/lib/Utils.js';

export const SelectionImage = ({ image, className }) => {
    return (
        <img
            src={`/view-image/${image}`}
            alt=""
            className={`max-w-20 max-h-72 mr-4 my-2 rounded-lg ${className}`}
        />
    );
};

const BlockDescription = ({ description }) => {
    const [showAll, setShowAll] = useState(false);
    const truncateLength = 400;
    const isTruncated = description?.length > truncateLength * 1.5 && !showAll;
    const displayDescription = isTruncated
        ? truncate(description, truncateLength)
        : description;

    return (
        <p className="whitespace-pre-line subhead mt-1">
            {displayDescription || ''}
            {isTruncated && (
                <div className="bg-gradient-fade-white-b -mt-6 w-full">
                    <button
                        className="themed-text font-medium text-xs uppercase block  pt-4"
                        onClick={e => {
                            //e.stopImmediatePropagation();
                            e.stopPropagation();
                            e.preventDefault();
                            setShowAll(!showAll);
                        }}
                        type="button"
                    >
                        See More{' '}
                        <ChevronDownIcon className="w-4 h-4 inline-block" />
                    </button>
                </div>
            )}
        </p>
    );
};

const PriceDisplay = ({ opt, className }) => {
    return (
        <div className={`mr-2 text-black font-semibold ${className}`}>
            {opt.price > 0 && !opt.discountedPrice
                ? formatCurrency(opt.price)
                : ''}

            {opt.price > 0 && opt.discountedPrice >= 0 && (
                <span>
                    <span className="line-through text-gray-500 mr-2">
                        {formatCurrency(opt.price)}
                    </span>{' '}
                    {formatCurrency(opt.discountedPrice)}
                </span>
            )}
        </div>
    );
};

const Chevron = ({ className }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className={`chevron ${className || ''}`}
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m8.25 4.5 7.5 7.5-7.5 7.5"
            />
        </svg>
    );
};

const SelectionBlock = ({
    options,
    value,
    onChange,
    selectedRightContent,
    hideCategoryTitle,
    numberSelectionsRequired = 1,
}) => {
    // Convert value to array for consistent handling
    const [selectedValues, setSelectedValues] = useState(
        Array.isArray(value) ? value : value ? [value] : [],
    );

    // Update selectedValues when value changes externally
    useEffect(() => {
        const newValues = Array.isArray(value) ? value : value ? [value] : [];
        setSelectedValues(newValues);
    }, [value]);

    // Find selected options based on selectedValues
    const selectedOptions = options.filter(o =>
        selectedValues.includes(o.value),
    );

    useEffect(() => {
        // If there's only 1 option then choose that by default
        if (
            selectedValues.length === 0 &&
            numberSelectionsRequired === 1 &&
            options.length === 1
        ) {
            const newValue = options[0].value;
            setSelectedValues([newValue]);
            onChange(newValue, options[0]);
        }
    }, [JSON.stringify(options)]);

    // Handle selection/deselection of an option
    const handleOptionClick = (optValue, opt) => {
        if (numberSelectionsRequired === 1 || opt.excludeFromMultiSelect) {
            // Single selection mode or excluded from multi-select
            setSelectedValues([optValue]);
            onChange(optValue, opt);
            return;
        }

        // Multiple selection mode
        let newSelectedValues;
        if (selectedValues.includes(optValue)) {
            // Deselect if already selected
            newSelectedValues = selectedValues.filter(v => v !== optValue);
        } else {
            // Select if not already selected
            newSelectedValues = [...selectedValues, optValue];
            // Limit to numberSelectionsRequired
            if (newSelectedValues.length > numberSelectionsRequired) {
                newSelectedValues.shift(); // Remove the first item
            }
        }

        setSelectedValues(newSelectedValues);

        // Only call onChange if we reached the required number of selections
        if (newSelectedValues.length === numberSelectionsRequired) {
            const selectedOpts = options.filter(o =>
                newSelectedValues.includes(o.value),
            );
            onChange(newSelectedValues, selectedOpts);
        } else if (newSelectedValues.length < selectedValues.length) {
            // If we're deselecting and now have fewer than required, notify parent
            onChange(null);
        }
    };

    const isOptionSelected = optValue => {
        return selectedValues.includes(optValue);
    };

    const optionsByCategory = {};
    for (const opt of options) {
        optionsByCategory[opt.category || ' '] = [
            ...(optionsByCategory[opt.category || ' '] || []),
            opt,
        ];
    }

    // Determine if we should show the selected view
    const showSelectedView =
        numberSelectionsRequired <= selectedValues.length || // We selected enough options
        // We selected an option that's excluded from multi select
        options.some(
            opt =>
                opt.excludeFromMultiSelect &&
                selectedValues.includes(opt.value),
        );
    return (
        <div>
            {showSelectedView && (
                <div>
                    <SelectionSelectedBlock
                        key={selectedOptions[0]?.value}
                        image={selectedOptions[0]?.image}
                        onChange={() => {
                            setSelectedValues([]);
                            onChange(null);
                        }}
                        title={selectedOptions.map(o => o.title).join(' and ')}
                        price={
                            selectedOptions[0]?.discountedPrice ||
                            selectedOptions[0]?.price
                        }
                        selectedRightContent={selectedRightContent}
                    />
                </div>
            )}
            {!showSelectedView && (
                <>
                    {Object.keys(optionsByCategory).map(category => (
                        <div key={category}>
                            {!hideCategoryTitle && (
                                <h3 className="mb-2 ml-4">{category}</h3>
                            )}
                            {optionsByCategory[category].map(opt => (
                                <section
                                    key={opt.value}
                                    className={`choose-block ${isOptionSelected(opt.value) ? ' ' : ''}`}
                                    onClick={() =>
                                        handleOptionClick(opt.value, opt)
                                    }
                                >
                                    {!!opt.image && (
                                        <div className="flex items-center justify-between">
                                            <SelectionImage image={opt.image} />
                                            <PriceDisplay
                                                opt={opt}
                                                className="block sm:hidden"
                                            />
                                        </div>
                                    )}

                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <p
                                                className={`${isOptionSelected(opt.value) ? 'font-bold' : 'font-medium'}`}
                                            >
                                                {opt.title || ''}
                                            </p>
                                            {!opt.image && (
                                                <PriceDisplay
                                                    opt={opt}
                                                    className={
                                                        'block sm:hidden'
                                                    }
                                                />
                                            )}
                                        </div>

                                        <p className="subhead">
                                            {opt.subtitle || ''}
                                        </p>
                                        {!!opt.description && (
                                            <BlockDescription
                                                description={opt.description}
                                            />
                                        )}
                                    </div>
                                    <div className="flex items-center">
                                        <PriceDisplay
                                            opt={opt}
                                            className={'hidden sm:block'}
                                        />

                                        {numberSelectionsRequired > 1 && (
                                            <>
                                                {isOptionSelected(
                                                    opt.value,
                                                ) && (
                                                    <span className="ml-2 uppercase block form-button btn-small !text-xs">
                                                        Selected
                                                    </span>
                                                )}
                                                {!isOptionSelected(
                                                    opt.value,
                                                ) && (
                                                    <Chevron className="hidden sm:block" />
                                                )}
                                            </>
                                        )}
                                        {numberSelectionsRequired === 1 && (
                                            <Chevron className="hidden sm:block" />
                                        )}
                                    </div>
                                </section>
                            ))}
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

export default SelectionBlock;
